import { DateType } from '../TimeSelector';
import dayjs from 'dayjs';
import type { RunStateDataItem } from './index';
import { formatDeviceUtilization } from '@/utils/utils';

// 生成Mock数据
export const generateMockRunStateData = (timeResolution: DateType): RunStateDataItem[] => {
  const now = dayjs();
  const data: RunStateDataItem[] = [];

  let count = 0;

  switch (timeResolution) {
    case DateType.YEAR:
      count = 12;
      break;
    case DateType.CUSTOM:
      count = 31;
      break;
    case DateType.MONTH:
    default:
      count = 30;
  }

  for (let i = 0; i < count; i++) {
    let time: number;
    switch (timeResolution) {
      case DateType.YEAR:
        time = now
          .startOf('year')
          .subtract(count - 1 - i, 'month')
          .valueOf();
        break;
      case DateType.CUSTOM:
        time = now.subtract(count - 1 - i, 'day').valueOf();
        break;
      case DateType.MONTH:
      default:
        time = now
          .startOf('month')
          .subtract(count - 1 - i, 'day')
          .valueOf();
    }

    const runningTime = Math.floor(Math.random() * 12) + 8;
    const standbyTime = Math.floor(Math.random() * 6) + 1;
    const shutdownTime = 24 - runningTime - standbyTime;
    const usageRate = Math.floor((runningTime / 24) * 100);

    data.push({
      time,
      runningTime,
      standbyTime,
      shutdownTime: Math.max(0, shutdownTime),
      usageRate,
    });
  }

  return data;
};

// X轴标签格式
const getXLabel = (timeResolution: DateType): string => {
  switch (timeResolution) {
    case DateType.YEAR:
      return 'YYYY-MM';
    case DateType.MONTH:
    case DateType.CUSTOM:
    default:
      return 'YYYY-MM-DD';
  }
};

// 获取图表配置
export const getChartOption = (data: RunStateDataItem[], timeResolution: DateType) => {
  const xLabel = getXLabel(timeResolution);

  return {
    color: ['#16DD8E', '#FFCB47', '#FF4D4F', '#2D8DFF'],
    grid: {
      left: 40,
      right: 40,
      top: 60,
      containLabel: true,
    },
    tooltip: {
      formatter: (tooltipData: any) => {
        return `
           <div style='width:170px'>
            <div style='color: rgba(255,255,255,0.65)'>${dayjs(Number(tooltipData[0]?.axisValue)).format(xLabel)}</div>
            <div>${tooltipData
              ?.map((item: any) => {
                // 如果是设备使用率，则使用特殊格式化
                const formattedValue =
                  item.seriesName === '设备使用率' ? formatDeviceUtilization(item.value) : item.value;
                const unit = item.seriesName !== '设备使用率' ? 'h' : '%';
                return `<div style='width: 174px;display: flex;align-items: center;margin-bottom: 4px;'><span style='display:inline-block;width:8px;height:8px;background:${
                  item?.color
                };border-radius:50%;margin-right:6px'></span> ${
                  item.seriesName ?? '--'
                }<span style='flex:1;text-align: right'>${formattedValue ?? '--'}${unit}</span></div></div>`;
              })
              .join('')}</div>
           </div>
          `;
      },
    },
    legend: [
      {
        top: 6,
        right: 10,
        bottom: 8,
        icon: 'rect',
        itemWidth: 16,
        itemHeight: 4,
        itemGap: 16,
        padding: [0, 8, 0, 0],
        data: [{ name: '设备使用率' }],
      },
      {
        top: 6,
        right: 107,
        bottom: 8,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 16,
        padding: [0, 8, 0, 0],
        data: [{ name: '运行时长' }, { name: '待机时长' }, { name: '停机时长' }],
      },
    ],
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        data: data.map(i => i.time),
        axisLabel: {
          formatter: (value: number) => dayjs(Number(value)).format(xLabel),
          margin: 8,
          lineHeight: 14,
        },
      },
    ],
    dataZoom: {
      type: 'slider',
      bottom: 20,
      height: 20,
      labelFormatter: (value: string) => {
        const time = data[Number(value)]?.time;
        return time ? dayjs(time).format(xLabel) : '';
      },
    },
    yAxis: [
      {
        type: 'value',
        name: '时长(h)',
        alignTicks: true,
        axisLine: {},
      },
      {
        type: 'value',
        name: '使用率(%)',
        nameGap: 8,
        axisLabel: {
          margin: 8,
          lineHeight: 20,
        },
      },
    ],
    series: [
      {
        name: '运行时长',
        type: 'bar',
        barMaxWidth: 80,
        yAxisIndex: 0,
        showSymbol: false,
        data: data.map(i => i.runningTime),
      },
      {
        name: '待机时长',
        type: 'bar',
        barMaxWidth: 80,
        showSymbol: false,
        data: data.map(i => i.standbyTime),
      },
      {
        name: '停机时长',
        type: 'bar',
        barMaxWidth: 80,
        showSymbol: false,
        data: data.map(i => i.shutdownTime),
      },
      {
        name: '设备使用率',
        smooth: true,
        type: 'line',
        yAxisIndex: 1,
        showSymbol: false,
        data: data.map(i => i.usageRate),
      },
    ],
  };
};
